import * as dateFns from "date-fns";
import clsx from "clsx";
import { Icon, Tooltip, type TagTone } from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- TODO: refactor to use a proper component
import type { TFunction } from "i18next";
import { useCurrentLocalization } from "@bfc-software/bfc-web-ui/i18n";
import styles from "./task-activity.module.css";
import { compareActivityEntries } from "../-data/activity-order";

import type {
  ActivityEntry,
  DeviceActionEntry,
  PickLine,
} from "../-data/types";

/** Mirrors `task-detail-drawer.tsx`'s `formatStarted` - kept local rather than
 * imported to avoid a circular import (the drawer imports `TaskActivity`
 * from this file). Same unparseable-falls-back-to-raw guard. */
function formatEventTime(value: string): string {
  const parsed = dateFns.parseISO(value);
  return dateFns.isValid(parsed) ? dateFns.format(parsed, "PP p") : value;
}

/**
 * The task detail drawer's Activity timeline (§9.1 `TaskActivity`).
 *
 * Rendered by `DrawerBody` only for an activated task (§11 Q5) - a planned
 * task has no history to show. `state` mirrors the same three-way shape the
 * pick Status column already draws for a single cell (`PickStatusCell`'s
 * `unknown` arm): here it is the whole section rather than one line, because
 * a history read failure is not attributable to any one pick.
 */
export type ActivityState =
  | { kind: "unavailable" } // history read failed - same affordance as pick `unknown`
  | { kind: "empty" } // history ok, zero entries (shouldn't happen but not fatal)
  | { kind: "loaded"; entries: readonly ActivityItem[] };

export interface ActivityItem {
  /** Stable key - event id or `${kind}-${occurredAt}` fallback. */
  id: string;
  /** Pre-formatted, e.g. "Slot scanned · A-01-03" (§11 Q3). */
  label: string;
  /** `null` -> no "by" line (§11 Q4: a `selectorNames` miss renders nothing). */
  by: string | null;
  /** ISO, rendered via `<time dateTime>`. */
  time: string;
  /** `formatStarted`-style display string. */
  timeLabel: string;
  /** Dot colour - success/info/warning/neutral/danger (§11 Q6). */
  tone: TagTone;
  /** Design always sets true today (L1317) - kept for parity, not dropped. */
  showDot: boolean;
}

/**
 * Exhaustive over `TagTone` (`tag.tsx:11-12`) - a member added to the union
 * fails to compile here, same discipline as `PICK_STATUS_TONES`.
 *
 * `TagTone` has no bare "neutral" role (`theme.css` only defines
 * `--color-neutral-container`/`on-neutral-container`), so neutral maps to
 * `bg-outline-strong` instead - a real role, distinct from the connector's
 * `bg-outline` below so the two don't visually merge.
 */
const DOT_TONE_CLASSES: Record<TagTone, string> = {
  neutral: "bg-outline-strong",
  info: "bg-info",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  accent: "bg-accent",
};

/** The i18n key under `tasks.taskDetail.drawer.activity.lifecycle` for each lifecycle kind. */
const LIFECYCLE_LABEL_KEYS: Record<
  "activated" | "assigned" | "started" | "completed",
  string
> = {
  activated: "activated",
  assigned: "assigned",
  started: "started",
  completed: "completed",
};

/** Dot tone per lifecycle kind (§11 Q6). */
const LIFECYCLE_TONES: Record<
  "activated" | "assigned" | "started" | "completed",
  TagTone
> = {
  activated: "neutral",
  assigned: "neutral",
  started: "info",
  completed: "success",
};

/** One `kind: "pick"` `ActivityEntry` arm, singled out for the grouping below. */
type PickActivityEntry = Extract<ActivityEntry, { kind: "pick" }>;

/** Every other `ActivityEntry` arm - what's left once `pick` is grouped away. */
type LifecycleActivityEntry = Exclude<ActivityEntry, { kind: "pick" }>;

/**
 * Whether a pick's device action is bad enough to mark its group danger (§11
 * Q6/R3): a scan that failed, a count with an exception, or a capture that
 * could not be confirmed. `weight_confirmation` has no failure arm at all - a
 * `TaskPickQuantRecord` of that kind is only ever written once a weight is
 * confirmed (see `toPickUnits` in `wire.ts`) - so it is never danger.
 */
function isDeviceActionDanger(action: DeviceActionEntry): boolean {
  switch (action.kind) {
    case "slot_scan":
    case "item_scan":
      return action.result.kind === "failed";
    case "lot_code_capture":
    case "expiration_date_capture":
      return action.captured.kind === "could_not_confirm";
    case "count_confirmation":
      return action.result.kind === "with_exception";
    case "weight_confirmation":
      return false;
  }
}

/** One lifecycle arm -> its `ActivityItem`, resolving its "by" line off the
 * page-wide selector directory (§11 Q4) - a miss (or a `null` `selectorId`)
 * renders no "by" line, since a ULID means nothing to a manager. */
function toLifecycleItem(
  entry: LifecycleActivityEntry,
  index: number,
  selectorNames: ReadonlyMap<string, string>,
  t: TFunction,
): ActivityItem {
  // `activated` carries no `selectorId` at all. `assigned`'s label bakes the
  // resolved name in directly (i18n's "Assigned to {{selectorName}}"), so it
  // carries no separate "by" line; `started`/`completed`'s labels carry no
  // name, so theirs is the "by" line instead (§11 Q4: a directory miss, or a
  // null `selectorId`, means no name to show either way).
  const resolvedName =
    entry.kind !== "activated" && entry.selectorId
      ? (selectorNames.get(entry.selectorId) ?? null)
      : null;
  // A reassignment names who lost the task; a directory miss drops "from", as for any "by".
  const reassignment = entry.kind === "assigned" ? entry.reassignment : null;
  const previousName = reassignment?.fromSelectorId
    ? (selectorNames.get(reassignment.fromSelectorId) ?? null)
    : null;

  return {
    id: `${entry.kind}-${entry.timestamp}-${index}`,
    label:
      entry.kind === "assigned"
        ? reassignment !== null
          ? [
              t(
                previousName === null
                  ? "tasks.taskDetail.drawer.activity.lifecycle.reassigned"
                  : "tasks.taskDetail.drawer.activity.lifecycle.reassignedFrom",
                { selectorName: resolvedName ?? "", previousName },
              ),
              reassignment.byUser === null
                ? null
                : t("tasks.taskDetail.drawer.activity.lifecycle.reassignedBy", {
                    actor: reassignment.byUser,
                  }),
            ]
              .filter((part) => part !== null)
              .join(" ")
          : t(`tasks.taskDetail.drawer.activity.lifecycle.assigned`, {
              selectorName: resolvedName ?? "",
            })
        : t(
            `tasks.taskDetail.drawer.activity.lifecycle.${LIFECYCLE_LABEL_KEYS[entry.kind]}`,
          ),
    by:
      entry.kind === "started" || entry.kind === "completed"
        ? resolvedName
        : null,
    time: entry.timestamp,
    timeLabel: formatEventTime(entry.timestamp),
    tone: LIFECYCLE_TONES[entry.kind],
    showDot: true,
  };
}

/** The group's last event in timeline order - not in input order, which is not guaranteed. */
function latestOf(events: readonly PickActivityEntry[]): PickActivityEntry {
  return events.reduce((latest, event) =>
    compareActivityEntries(event, latest) >= 0 ? event : latest,
  );
}

/**
 * One pick line's group of `kind: "pick"` events -> a single `ActivityItem`
 * (§11 R3, superseding Q3's one-entry-per-device-action). Its timestamp is
 * the group's LAST event's `occurredAt` - the device's own time, shown as it
 * was; "last" is by the ordering in `activity-order.ts`, which is the server's
 * clock. Its "by" is that same last event's selector;
 * its label joins `pickId` to the matching `PickLine` for `#seq itemId` - a
 * `pickId` no `PickLine` claims (shouldn't happen, but not fatal) falls back
 * to a bare "Pick". Danger if ANY event in the group failed a scan, had a
 * count exception, or could not confirm a capture.
 */
function toPickGroupItem(
  pickId: string,
  events: readonly PickActivityEntry[],
  pickLineByPickId: ReadonlyMap<string, PickLine>,
  selectorNames: ReadonlyMap<string, string>,
  t: TFunction,
): ActivityItem {
  const last = latestOf(events);
  const pickLine = pickLineByPickId.get(pickId);
  return {
    id: `pick-${pickId}`,
    label: pickLine
      ? t("tasks.taskDetail.drawer.activity.pick", {
          seq: pickLine.seq,
          itemId: pickLine.itemId,
        })
      : t("tasks.taskDetail.drawer.activity.pickFallback"),
    by: selectorNames.get(last.selectorId) ?? null,
    time: last.occurredAt,
    timeLabel: formatEventTime(last.occurredAt),
    tone: events.some((event) => isDeviceActionDanger(event.action))
      ? "danger"
      : "neutral",
    showDot: true,
  };
}

/**
 * Builds the view-shaped `ActivityItem` list from step 1's `ActivityEntry[]`
 * (§11 Q3/Q4, R3): every `kind: "pick"` arm is grouped by `pick_id` into one
 * "Pick" entry per pick line, joined here to `picks` (not in `wire.ts`, which
 * stays the pure per-event mapping layer) for the seq/itemId a label needs;
 * every other arm passes through one-for-one.
 *
 * Sorted by `compareActivityEntries` - the server's clock, then position in the
 * task's life - not by the `time` an item displays: a pick shows its device's
 * time, which can run ahead of the lifecycle events' stamps and would put a
 * pick underneath the "Task completed" it preceded. A group sorts as its LAST
 * event does. Ties past that keep input order.
 */
export function toActivityItems(
  entries: readonly ActivityEntry[],
  picks: readonly PickLine[],
  selectorNames: ReadonlyMap<string, string>,
  t: TFunction,
): ActivityItem[] {
  const pickLineByPickId = new Map(picks.map((pick) => [pick.id, pick]));
  const pickGroups = new Map<
    string,
    { events: PickActivityEntry[]; order: number }
  >();
  // `entry` is what the item sorts as: the lifecycle entry itself, or a pick
  // group's last event.
  const staged: { item: ActivityItem; entry: ActivityEntry; order: number }[] =
    [];

  entries.forEach((entry, order) => {
    if (entry.kind === "pick") {
      const group = pickGroups.get(entry.pickId) ?? { events: [], order };
      group.events.push(entry);
      group.order = order; // entries arrive pre-sorted ascending, so the last
      // one seen for a given pickId is genuinely its group's last event.
      pickGroups.set(entry.pickId, group);
      return;
    }
    staged.push({
      item: toLifecycleItem(entry, order, selectorNames, t),
      entry,
      order,
    });
  });

  for (const [pickId, group] of pickGroups) {
    staged.push({
      item: toPickGroupItem(
        pickId,
        group.events,
        pickLineByPickId,
        selectorNames,
        t,
      ),
      entry: latestOf(group.events),
      order: group.order,
    });
  }

  return staged
    .sort(
      (a, b) => compareActivityEntries(a.entry, b.entry) || a.order - b.order,
    )
    .map(({ item }) => item);
}

export function TaskActivity({
  state,
}: {
  state: ActivityState;
}): React.ReactElement {
  const [t] = useCurrentLocalization();
  if (state.kind === "unavailable") {
    // Same affordance as `PickStatusCell`'s `unknown` arm - icon + `Tooltip` +
    // sr-only label, danger tone (§11 Q5).
    return (
      <span
        className="inline-flex text-danger"
        data-test-role="task-detail-activity-unavailable"
      >
        <Tooltip content={t("tasks.taskDetail.drawer.activityUnavailable")}>
          <Icon name="AlertCircle" size={16} />
          <span className="sr-only">
            {t("tasks.taskDetail.drawer.activityUnavailableLabel")}
          </span>
        </Tooltip>
      </span>
    );
  }

  if (state.kind === "empty") {
    return (
      <p
        className="text-on-surface-variant"
        data-test-role="task-detail-activity-empty"
      >
        {t("tasks.taskDetail.drawer.activityEmpty")}
      </p>
    );
  }

  return (
    <ol className="flex flex-col" data-test-role="task-detail-activity">
      {state.entries.map((ev) => (
        <li
          key={ev.id}
          className="flex"
          data-test-role="task-detail-activity-item"
        >
          <div className="flex flex-col items-center">
            {ev.showDot ? (
              <span
                className={clsx(
                  "size-8 rounded-full",
                  DOT_TONE_CLASSES[ev.tone],
                  styles.activityDotTransform,
                )}
              />
            ) : null}
            <span className="flex-1 border-l-1 border-neutral-container" />
          </div>
          <div className="ml-12 pb-16">
            <div className="text-size-14 font-600 text-on-surface">
              {ev.label}
            </div>
            {ev.by ? (
              <div className="text-size-12 text-on-surface-variant">
                {ev.by}
              </div>
            ) : null}
            <time
              dateTime={ev.time}
              className="text-size-12 text-on-surface-variant"
            >
              {ev.timeLabel}
            </time>
          </div>
        </li>
      ))}
    </ol>
  );
}
